import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Star, ChevronDown, ChevronUp, Search, Users, Calendar, Shield, MapPin, Trophy, Camera } from 'lucide-react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { useToast } from '../../context/ToastContext';
import { resizeImageToBase64 } from '../../utils/imageUtils';
import { geocodeCity } from '../../utils/geo';
import Button from '../../components/ui/Button';
import StarRating from '../../components/ui/StarRating';
import CityAutocomplete from '../../components/ui/CityAutocomplete';
import { EMOJI_OPTIONS } from '../../constants/data';
import TeamPicker from '../../components/ui/TeamPicker';

/* Collapsible emoji grid — same pattern as ProfilePage AvatarPickerRadio */
const AvatarPickerCollapsible = ({ value, noPhoto, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div
        className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl p-3 cursor-pointer"
        onClick={() => setOpen(o => !o)}
      >
        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-2xl shadow-inner border border-slate-200 shrink-0 select-none">
          {noPhoto ? value : '📷'}
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold text-slate-700">Avatar emoji</div>
          <span className="text-xs text-blue-600 font-medium">{open ? '▲ Fechar' : '▼ Trocar avatar'}</span>
        </div>
      </div>
      {open && (
        <div className="mt-1.5 grid grid-cols-8 gap-1.5 bg-slate-50 p-3 rounded-xl border border-slate-100">
          {EMOJI_OPTIONS.map(emoji => (
            <button
              key={emoji}
              type="button"
              onClick={() => { onChange(emoji); setOpen(false); }}
              className={`h-10 flex items-center justify-center text-xl rounded-lg transition-all ${value === emoji && noPhoto ? 'bg-blue-500' : 'hover:bg-slate-200'}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const maskPhone = (v) => v.replace(/\D/g,'').replace(/(\d{2})(\d)/,'($1) $2').replace(/(\d{5})(\d)/,'$1-$2').slice(0,15);

/* Phone input with fixed +55 country code */
const PhoneInput55 = ({ value, onChange, placeholder = '(xx) 9xxxx-xxxx' }) => (
  <div className="flex">
    <span className="flex items-center gap-1 px-3 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-sm font-medium text-slate-600 whitespace-nowrap">
      🇧🇷 +55
    </span>
    <input
      type="tel"
      className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-r-xl outline-none"
      placeholder={placeholder}
      value={value}
      maxLength={15}
      onChange={e => onChange(maskPhone(e.target.value))}
    />
  </div>
);

const ABOUT_FEATURES = [
  { icon: Search, label: 'Encontre peladas públicas na sua cidade' },
  { icon: Users, label: 'Gerencie grupos e confirme presenças' },
  { icon: Calendar, label: 'Sorteio de times equilibrado por nível' },
  { icon: Shield, label: 'Check-in externo com aprovação do admin' },
  { icon: Star, label: 'Avaliação pós-jogo e ranking da comunidade' },
  { icon: MapPin, label: 'Localização da quadra integrada ao Maps' },
  { icon: Trophy, label: 'Craque e bola murcha ao final de cada jogo' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  // ─── UI state ───
  const [isRegistering, setIsRegistering] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── Email login ───
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // ─── Registration form ───
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '',
    position: 'Meia', manualRating: 5, heartTeam: 'Sem Time',
    dominantFoot: 'Destro', photo: '👤', city: '',
  });
  const [regPhotoFile, setRegPhotoFile] = useState(null);
  const [regPhotoPreview, setRegPhotoPreview] = useState(null);
  const regFileInputRef = useRef(null);

  // ─── Handlers ───
  const saveProfile = async (uid, data, photoURL = null) => {
    const coords = data.city ? await geocodeCity(data.city).catch(() => null) : null;
    // Campos públicos — visíveis para outros utilizadores autenticados
    await setDoc(doc(db, 'users', uid, 'profile', 'data'), {
      name: data.name,
      photo: data.photo,
      photoURL: photoURL || null,
      position: data.position,
      manualRating: parseFloat(data.manualRating),
      communityRating: null,
      heartTeam: data.heartTeam,
      dominantFoot: data.dominantFoot,
      city: data.city || '',
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      createdAt: serverTimestamp(),
    });
    // Campos sensíveis — apenas o próprio user pode ler
    await setDoc(doc(db, 'users', uid, 'profile', 'private'), {
      phone: data.phone || '',
      email: data.email || '',
    });
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) return;
    setSubmitting(true);
    try {
      let photoURL = null;
      if (regPhotoFile) photoURL = await resizeImageToBase64(regPhotoFile);
      const cred = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      await saveProfile(cred.user.uid, formData, photoURL);
      navigate('/');
    } catch (err) {
      const messages = {
        'auth/email-already-in-use': 'Este email já está em uso.',
        'auth/weak-password': 'A senha deve ter pelo menos 6 caracteres.',
        'auth/invalid-email': 'Email inválido.',
      };
      showError(messages[err.code] || 'Erro ao criar conta.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      navigate('/');
    } catch (err) {
      const messages = {
        'auth/user-not-found': 'Email não encontrado.',
        'auth/wrong-password': 'Senha incorreta.',
        'auth/invalid-email': 'Email inválido.',
        'auth/invalid-credential': 'Email ou senha incorretos.',
      };
      showError(messages[err.code] || 'Erro ao entrar.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail) { showError('Digite seu email primeiro.'); return; }
    try {
      await sendPasswordResetEmail(auth, loginEmail);
      showSuccess('Email de recuperação enviado!');
    } catch (err) {
      const messages = {
        'auth/user-not-found': 'Email não encontrado.',
        'auth/invalid-email': 'Email inválido.',
      };
      showError(messages[err.code] || 'Erro ao enviar email.');
    }
  };

  // ─── Render ───
  return (
    <div
      className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <img src="/icone.png" alt="Pelada Match" className="w-28 h-28 mb-4 drop-shadow-lg" />
      <h1 className="text-2xl font-bold text-white mb-1">pelada<span className="text-blue-400">match</span></h1>
      <p className="text-slate-400 text-sm mb-6">O futebol raiz acontecendo</p>

      <div className="w-full max-w-sm bg-white rounded-xl p-6 shadow-xl">

        {isRegistering ? (
          /* ─── Registration form ─── */
          <form onSubmit={handleRegisterSubmit} className="space-y-4">
            <h2 className="text-lg font-bold text-slate-800 text-center">Criar Conta</h2>

            {/* ── Foto + Avatar (mesmo padrão do ProfilePage) ── */}
            <div className="flex items-center gap-4 bg-slate-50 border border-slate-100 rounded-xl p-3">
              <div className="relative shrink-0 cursor-pointer" onClick={() => regFileInputRef.current?.click()}>
                {regPhotoPreview ? (
                  <img src={regPhotoPreview} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-slate-200" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-3xl shadow-inner border border-slate-200">{formData.photo}</div>
                )}
                <div className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-sm">
                  <Camera className="w-3 h-3 text-white" />
                </div>
              </div>
              <div>
                <div className="text-sm font-bold text-slate-700">Foto de perfil</div>
                <button type="button" onClick={() => regFileInputRef.current?.click()} className="text-xs text-blue-600 font-medium">Enviar foto</button>
                {regPhotoPreview && (
                  <button type="button" onClick={() => { setRegPhotoPreview(null); setRegPhotoFile(null); }} className="ml-2 text-xs text-red-400 font-medium">Remover</button>
                )}
              </div>
            </div>
            <input ref={regFileInputRef} type="file" accept="image/*" onChange={e => {
              const file = e.target.files?.[0];
              if (!file) return;
              setRegPhotoFile(file);
              const reader = new FileReader();
              reader.onload = (ev) => setRegPhotoPreview(ev.target.result);
              reader.readAsDataURL(file);
            }} className="hidden" />

            {/* Avatar emoji — colapsível */}
            <AvatarPickerCollapsible
              value={formData.photo}
              noPhoto={!regPhotoPreview}
              onChange={v => { setFormData({ ...formData, photo: v }); setRegPhotoPreview(null); setRegPhotoFile(null); }}
            />

            {/* Dados da conta */}
            <input className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" placeholder="Nome" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            <input className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" placeholder="Email" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
              <input className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" placeholder="Senha (mín. 6 caracteres)" type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required minLength={6} />
            </div>
            <PhoneInput55
              value={formData.phone}
              onChange={v => setFormData({ ...formData, phone: v })}
            />
            <CityAutocomplete
              value={formData.city}
              onChange={v => setFormData({ ...formData, city: v })}
            />

            {/* Posição — linha própria, full width */}
            <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" value={formData.position} onChange={e => setFormData({ ...formData, position: e.target.value })}>
              <option value="Goleiro">Goleiro</option>
              <option value="Defensor">Defensor</option>
              <option value="Meia">Meia</option>
              <option value="Atacante">Atacante</option>
            </select>

            {/* Time do coração — linha própria, full width */}
            <TeamPicker value={formData.heartTeam} onChange={team => setFormData({ ...formData, heartTeam: team })} />

            {/* Auto-avaliação */}
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex flex-col items-center">
              <label className="text-xs font-bold text-slate-500 mb-2">Seu Nível (Auto-avaliação)</label>
              <StarRating rating={formData.manualRating} setRating={v => setFormData({ ...formData, manualRating: v })} />
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>{submitting ? 'Criando...' : 'Cadastrar'}</Button>
            <div className="text-center"><button type="button" onClick={() => setIsRegistering(false)} className="text-sm text-slate-500 hover:text-blue-600">Já tem conta? Entrar</button></div>
          </form>

        ) : (
          /* ─── Login form ─── */
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <h2 className="text-lg font-bold text-slate-800 text-center">Entrar</h2>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
              <input className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" placeholder="Seu email" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
              <input className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" placeholder="Senha" type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>{submitting ? 'Entrando...' : 'Entrar'}</Button>
            <div className="text-center"><button type="button" onClick={handleForgotPassword} className="text-sm text-blue-500 hover:text-blue-600">Esqueci minha senha</button></div>
            <div className="text-center"><button type="button" onClick={() => setIsRegistering(true)} className="text-sm text-slate-500 hover:text-blue-600">Não tem conta? Cadastrar</button></div>
          </form>
        )}
      </div>

      {/* ─── Sobre o App accordion ─── */}
      {!isRegistering && (
        <div className="w-full max-w-sm mt-4">
          <button
            type="button"
            onClick={() => setShowAbout(!showAbout)}
            className="w-full flex items-center justify-center gap-2 text-slate-500 text-sm py-2 hover:text-slate-300 transition-colors"
          >
            {showAbout ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Sobre o Pelada Match
          </button>
          {showAbout && (
            <div className="bg-slate-800 rounded-xl p-4 mt-1 space-y-3">
              <p className="text-slate-400 text-xs text-center">A plataforma para quem leva o fut a sério</p>
              {ABOUT_FEATURES.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className="w-7 h-7 bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <span className="text-slate-300 text-xs">{label}</span>
                </div>
              ))}
              <p className="text-slate-600 text-[10px] text-center pt-1">v2.2.0</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoginPage;
